import {
  AUDIENCE_DOCUMENT_IDS,
  SITE_IDS,
} from '@pkg/config/sanity/audience';
import { isBusinessDocumentType } from '@pkg/config/sanity/documentTypesByWorkspace';

/**
 * Normalize a public site origin from runtimeConfig (trim, strip trailing slash,
 * ensure http(s) scheme).
 */
export function normalizePublicOrigin(domain?: string | null): string {
  const value = String(domain || '').trim().replace(/\/+$/, '');
  if (!value) {
    return '';
  }
  return value.startsWith('http') ? value : `https://${value}`;
}

function normalizeAudienceRef(ref: string | null | undefined): string {
  return typeof ref === 'string' ? ref.replace(/^drafts\./, '') : '';
}

/**
 * True when the linked page should be served by the business site.
 * Uses stored audience when present; otherwise infers from the workspace registry.
 */
export function isBusinessAudienceTarget(
  targetAudienceRef: string | null | undefined,
  targetDocumentType?: string | null,
): boolean {
  const ref = normalizeAudienceRef(targetAudienceRef);
  if (ref === AUDIENCE_DOCUMENT_IDS[SITE_IDS.BUSINESS]) {
    return true;
  }
  if (ref) {
    return false;
  }
  return Boolean(targetDocumentType && isBusinessDocumentType(targetDocumentType));
}

/**
 * True when the linked page should be served by the consumer (www) site.
 * Uses stored audience when present; otherwise infers from the workspace registry
 * (www-only types like `legalPages`, and untagged non-business docs → www).
 */
export function isWwwAudienceTarget(
  targetAudienceRef: string | null | undefined,
  targetDocumentType?: string | null,
): boolean {
  return !isBusinessAudienceTarget(targetAudienceRef, targetDocumentType);
}

/**
 * Business hosts only business-audience routes. Common, www, and untagged
 * www-owned docs (e.g. legalPages) are served by the consumer site — links
 * from business must go absolute to www.
 */
export function isWwwCrossSiteTarget(
  currentSiteId: string | undefined,
  targetAudienceRef: string | null | undefined,
  targetDocumentType?: string | null,
): boolean {
  if (currentSiteId !== SITE_IDS.BUSINESS) {
    return false;
  }

  return isWwwAudienceTarget(targetAudienceRef, targetDocumentType);
}

/**
 * WWW hosts consumer routes. Business-audience pages live on the business site —
 * links from www (or unset siteId) must go absolute to business.
 *
 * No-op unless audience separation is enabled (`SANITY_STUDIO_BUSINESS_WORKSPACE_ENABLED`).
 */
export function isBusinessCrossSiteTarget(
  currentSiteId: string | undefined,
  targetAudienceRef: string | null | undefined,
  targetDocumentType?: string | null,
  audienceSeparationEnabled = false,
): boolean {
  if (!audienceSeparationEnabled || currentSiteId === SITE_IDS.BUSINESS) {
    return false;
  }

  return isBusinessAudienceTarget(targetAudienceRef, targetDocumentType);
}

/**
 * Build an absolute URL on another site origin, preserving path/query/hash.
 */
export function buildAbsoluteSiteUrl(
  origin: string,
  path: string,
  query?: Record<string, unknown>,
  hash?: string | null,
): string {
  const base = normalizePublicOrigin(origin);
  const pathname = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(pathname, `${base}/`);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value == null) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const entry of value) {
          if (entry != null) {
            url.searchParams.append(key, String(entry));
          }
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }

  if (hash) {
    url.hash = hash.startsWith('#') ? hash.slice(1) : hash;
  }

  return url.toString();
}
